import type { PropsWithChildren } from 'react';
import React, { useCallback, useRef } from 'react';
import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import styles from 'intl-tel-input/build/css/intlTelInput.min.css';
import { themeGet } from 'styled-system';

import arrowDown from '../../assets/icons/arrow-down.svg';
import { MODAL_Z_INDEX } from '../../constants/z-index';
import { breakpoint } from '../../theme/helper';
import { focusOutlineCSS } from '../../theme/styles';
import type { IntlTelInputChangeResult } from '../../types/tel';
import Input from '../Input/StatefulInput';
import { LazyStylesComponent } from '../LazyStylesComponent';
import { useIti } from './hooks';

interface InputStateProps {
  hasError?: boolean;
  hasWarning?: boolean;
  hasSuccess?: boolean;
  errors?: string[];
  warnings?: string[];
  successes?: string[];
}

interface IntlTelInputProps extends InputStateProps {
  id: string;
  placeholder: string;
  label: string;
  name: string;
  required?: boolean;
  initialValue: string;
  onChange: (value: Nullable<IntlTelInputChangeResult>) => void;
  onBlur: (value: Nullable<IntlTelInputChangeResult>) => void;
  className?: string;
  inputRef?: React.Ref<HTMLInputElement>;
}

const ARROW_SIZE = 20;
const CONTAINER_ZINDEX = MODAL_Z_INDEX + 1;

const globalStyles = css`
  .iti.iti--container {
    z-index: ${CONTAINER_ZINDEX};
  }
`;

const Container = styled.div<PropsWithChildren>`
  width: 100%;

  .iti {
    width: 100%;
    --iti-hover-color: transparent;
  }

  .iti__country.iti__highlight {
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
  }

  .iti__selected-dial-code {
    color: ${themeGet('colors.sys.neutral.text.strong')};
    margin-left: ${themeGet('space.sp_4')}px;
  }

  .iti__dial-code {
    color: ${themeGet('colors.sys.neutral.text.strong')};
  }

  .iti__arrow {
    min-width: ${ARROW_SIZE}px;
    min-height: ${ARROW_SIZE}px;
    border: none;
    background-image: url(${arrowDown});
    background-size: contain;
  }

  .iti__arrow--up {
    transform: scaleY(-1);
  }

  .iti button {
    border-radius: ${themeGet('radii.small')};
    padding-right: ${themeGet('space.sp_4')}px;
    padding-left: ${themeGet('space.sp_8')}px;

    ${focusOutlineCSS}
  }

  .iti > input {
    margin-bottom: 0;
  }

  ${breakpoint(2)} {
    .iti button {
      font-size: ${themeGet('fontSizes.fs_18')}px;
      padding-left: ${themeGet('space.sp_10')}px;
    }
  }
`;

const IntlTelInput = ({
  id,
  placeholder,
  label,
  name,
  initialValue = '',
  hasError,
  hasWarning,
  hasSuccess,
  errors,
  warnings,
  successes,
  required,
  onChange,
  onBlur,
  className,
  inputRef,
}: IntlTelInputProps) => {
  const inputStateProps = {
    hasError,
    hasWarning,
    hasSuccess,
    errors,
    warnings,
    successes,
  };
  const elementRef = useRef<HTMLInputElement>(null);
  const { inputValue, handleChange, handleBlur } = useIti({
    elementRef,
    initialValue,
    onChange,
    onBlur,
  });

  const setRefs = useCallback(
    (node: HTMLInputElement | null) => {
      elementRef.current = node;

      if (typeof inputRef === 'function') {
        inputRef(node);
      } else if (inputRef) {
        (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    },
    [inputRef]
  );

  return (
    <>
      <LazyStylesComponent identifier="intl-tel-input" styles={styles}>
        <Container>
          <Input
            type="tel"
            id={id}
            inputRef={setRefs}
            name={name}
            aria-label={label}
            aria-required={required ?? false}
            placeholder={placeholder}
            label={label}
            onInput={handleChange}
            onBlur={handleBlur}
            className={className}
            autoComplete="tel"
            value={inputValue}
            {...inputStateProps}
          />
        </Container>
      </LazyStylesComponent>
      <Global styles={globalStyles} />
    </>
  );
};

export default IntlTelInput;
